import {
	CORNERS,
	cornersCrossed,
	GEARS,
	LADDER_COLUMNS,
	LADDER_ROWS,
	LADDER_SQUARES,
	LADDERS,
	landingFor,
	type PlayerIndex,
	SNAKES,
	type SnakesLaddersState,
	squarePosition,
	TRACK_LENGTH,
	type TurboTrackState,
} from "@playpal/core";
import { useEffect, useRef, useState } from "react";
import { Die } from "../components/Die";

interface BoardProps<S> {
	state: S;
	seat: PlayerIndex;
	onMove: (move: unknown) => void;
}

// --- Snakes and Ladders ---------------------------------------------------

/** One cell's edge in viewBox units. The whole board is 100 wide. */
const CELL = 100 / LADDER_COLUMNS;
/** The viewBox height: six rows of ten-unit cells. */
const BOARD_H = LADDER_ROWS * CELL;

/** A square's centre in viewBox units. */
function pt(square: number): { x: number; y: number } {
	const spot = squarePosition(square);
	return { x: spot.x * 100, y: spot.y * BOARD_H };
}

/**
 * Soft row tints, cycling up the board so every row feels like its own street.
 * Checkerboarded against near-white cells so the numbers stay readable.
 */
const ROW_TINTS = [
	"fill-amber-200/70 dark:fill-amber-400/15",
	"fill-sky-200/70 dark:fill-sky-400/15",
	"fill-rose-200/70 dark:fill-rose-400/15",
	"fill-emerald-200/70 dark:fill-emerald-400/15",
	"fill-violet-200/70 dark:fill-violet-400/15",
	"fill-orange-200/70 dark:fill-orange-400/15",
];

/** Three friendly snake colourways, dealt round the board in turn. */
const SNAKE_TONES = [
	{
		outline: "stroke-emerald-700 dark:stroke-emerald-800",
		body: "stroke-emerald-400",
		head: "fill-emerald-400",
		headRim: "stroke-emerald-700 dark:stroke-emerald-800",
	},
	{
		outline: "stroke-fuchsia-800 dark:stroke-fuchsia-900",
		body: "stroke-fuchsia-400",
		head: "fill-fuchsia-400",
		headRim: "stroke-fuchsia-800 dark:stroke-fuchsia-900",
	},
	{
		outline: "stroke-sky-700 dark:stroke-sky-800",
		body: "stroke-sky-400",
		head: "fill-sky-400",
		headRim: "stroke-sky-700 dark:stroke-sky-800",
	},
];

/** A ladder: two rails and their rungs, between the real squares it joins. */
function Ladder({ from, to }: { from: number; to: number }) {
	const a = pt(from);
	const b = pt(to);
	const dx = b.x - a.x;
	const dy = b.y - a.y;
	const length = Math.hypot(dx, dy);
	const ux = dx / length;
	const uy = dy / length;
	// Perpendicular, for setting the rails either side of the centre line.
	const nx = -uy;
	const ny = ux;
	const off = 1.7;

	const rungCount = Math.max(3, Math.round(length / 4.5));
	const rungs = Array.from({ length: rungCount }, (_, i) => {
		const t = (i + 0.5) / rungCount;
		return { x: a.x + dx * t, y: a.y + dy * t };
	});

	return (
		<g>
			{[off, -off].map((o) => (
				<line
					key={o}
					x1={a.x + nx * o}
					y1={a.y + ny * o}
					x2={b.x + nx * o}
					y2={b.y + ny * o}
					className="stroke-amber-600 dark:stroke-amber-500"
					strokeWidth={1.15}
					strokeLinecap="round"
				/>
			))}
			{rungs.map((r) => (
				<line
					key={`${r.x}-${r.y}`}
					x1={r.x + nx * off}
					y1={r.y + ny * off}
					x2={r.x - nx * off}
					y2={r.y - ny * off}
					className="stroke-amber-500 dark:stroke-amber-400"
					strokeWidth={0.9}
					strokeLinecap="round"
				/>
			))}
		</g>
	);
}

/**
 * A snake: a smooth S-curved body from its head square down to its tail, with
 * eyes, a snout and a forked tongue. Friendly on purpose — round shapes, big
 * eyes, bright colours — because the child it bites is eight.
 */
function Snake({ from, to, tone }: { from: number; to: number; tone: (typeof SNAKE_TONES)[0] }) {
	const h = pt(from);
	const t = pt(to);
	const dx = t.x - h.x;
	const dy = t.y - h.y;
	const length = Math.hypot(dx, dy);
	const nx = -dy / length;
	const ny = dx / length;
	// How far the body bows out to each side; capped so short snakes stay tidy.
	const bow = Math.min(8, length * 0.32);

	const c1 = { x: h.x + dx * 0.3 + nx * bow, y: h.y + dy * 0.3 + ny * bow };
	const c2 = { x: h.x + dx * 0.7 - nx * bow, y: h.y + dy * 0.7 - ny * bow };
	const d = `M ${h.x} ${h.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${t.x} ${t.y}`;

	// The way the head faces: back along the curve's opening direction.
	const fLen = Math.hypot(h.x - c1.x, h.y - c1.y);
	const fx = (h.x - c1.x) / fLen;
	const fy = (h.y - c1.y) / fLen;
	// Perpendicular to the face, for placing the two eyes.
	const ex = -fy;
	const ey = fx;
	const head = { x: h.x + fx * 0.5, y: h.y + fy * 0.5 };
	const tip = { x: head.x + fx * 2.5, y: head.y + fy * 2.5 };
	const mid = { x: head.x + fx * 3.5, y: head.y + fy * 3.5 };

	return (
		<g>
			<path d={d} fill="none" className={tone.outline} strokeWidth={4} strokeLinecap="round" />
			<path d={d} fill="none" className={tone.body} strokeWidth={2.7} strokeLinecap="round" />
			<path
				d={d}
				fill="none"
				className="stroke-white/60"
				strokeWidth={0.7}
				strokeLinecap="round"
				strokeDasharray="1.4 2.4"
			/>
			{/* Forked tongue, drawn under the head so it pokes out of the mouth. */}
			<path
				d={`M ${head.x + fx * 1.8} ${head.y + fy * 1.8} L ${tip.x} ${tip.y} M ${tip.x} ${tip.y} L ${mid.x + ex * 0.8} ${mid.y + ey * 0.8} M ${tip.x} ${tip.y} L ${mid.x - ex * 0.8} ${mid.y - ey * 0.8}`}
				fill="none"
				className="stroke-rose-500"
				strokeWidth={0.55}
				strokeLinecap="round"
			/>
			<circle
				cx={head.x}
				cy={head.y}
				r={2.4}
				className={`${tone.head} ${tone.headRim}`}
				strokeWidth={0.6}
			/>
			{[1, -1].map((side) => (
				<g key={side}>
					<circle
						cx={head.x + fx * 0.7 + ex * side}
						cy={head.y + fy * 0.7 + ey * side}
						r={0.85}
						className="fill-white"
					/>
					<circle
						cx={head.x + fx * 0.95 + ex * side}
						cy={head.y + fy * 0.95 + ey * side}
						r={0.42}
						className="fill-slate-900"
					/>
				</g>
			))}
		</g>
	);
}

/**
 * Where a counter that has just arrived on `to` slid from, or null if it walked
 * there. The state only stores where a player ended up; if that is a ladder's
 * top or a snake's tail, and its foot or head was within a die's throw of where
 * they stood, they must have travelled along it — and the token should too.
 */
function slideOrigin(from: number, to: number): number | null {
	const links: [string, number][] = [...Object.entries(LADDERS), ...Object.entries(SNAKES)];
	for (const [headText, tail] of links) {
		const head = Number(headText);
		if (tail !== to) continue;
		for (let step = 1; step <= 6; step++) {
			const raw = from + step;
			const landed = raw > LADDER_SQUARES ? LADDER_SQUARES - (raw - LADDER_SQUARES) : raw;
			if (landed === head) return head;
		}
	}
	return null;
}

/**
 * The square a counter is drawn on, which trails the square it is on. A move
 * that ends down a snake or up a ladder shows in two stages — walk to the
 * head or foot, pause, then travel to the far end — so the slide reads as
 * movement along the thing rather than a teleport.
 */
function useSlideAlong(square: number): number {
	const [shown, setShown] = useState(square);
	const prev = useRef(square);

	useEffect(() => {
		const from = prev.current;
		prev.current = square;
		if (square === from) return;
		const origin = slideOrigin(from, square);
		if (origin === null) {
			setShown(square);
			return;
		}
		setShown(origin);
		const timer = setTimeout(() => setShown(square), 720);
		return () => clearTimeout(timer);
	}, [square]);

	return shown;
}

/** A player's counter: a glossy disc that glides from square to square. */
function Token({ square, me }: { square: number; me: boolean }) {
	const shown = useSlideAlong(square);
	if (square < 1) return null;
	const p = pt(Math.max(1, shown));

	return (
		<g
			data-testid={me ? "sl-token-me" : "sl-token-them"}
			className="pointer-events-none"
			style={{
				transform: `translate(${p.x}px, ${p.y}px)`,
				transition: "transform 680ms cubic-bezier(0.35, 0.1, 0.3, 1)",
			}}
		>
			<ellipse cx={0.4} cy={0.9} rx={3} ry={2.6} className="fill-black/25" />
			<circle
				r={2.9}
				fill={me ? "var(--color-p1)" : "var(--color-p2)"}
				className="stroke-white"
				strokeWidth={0.7}
			/>
			<circle cx={-0.9} cy={-1} r={0.8} className="fill-white/70" />
		</g>
	);
}

export function SnakesLaddersBoard({ state, seat, onMove }: BoardProps<SnakesLaddersState>) {
	const myTurn = state.turn === seat && !state.result;
	const rival: PlayerIndex = seat === 0 ? 1 : 0;

	return (
		<div className="board-frame board-md">
			<div className="mb-3 overflow-hidden rounded-2xl bg-gradient-to-b from-sky-100 to-indigo-100 shadow-lg ring-4 ring-indigo-300/60 dark:from-indigo-950 dark:to-slate-900 dark:ring-indigo-500/30">
				<svg
					viewBox={`0 0 100 ${BOARD_H}`}
					className="block w-full"
					role="img"
					aria-label="Snakes and ladders board"
					data-testid="sl-board"
				>
					{/* The squares, numbered 1 at the bottom left up to the finish. */}
					{Array.from({ length: LADDER_SQUARES }, (_, i) => {
						const square = i + 1;
						const centre = pt(square);
						const x = centre.x - CELL / 2;
						const y = centre.y - CELL / 2;
						const row = Math.floor(i / LADDER_COLUMNS);
						const col = Math.round(centre.x / CELL - 0.5);
						const tinted = (row + col) % 2 === 0;
						const start = square === 1;
						const finish = square === LADDER_SQUARES;

						return (
							<g key={square}>
								<rect
									x={x + 0.25}
									y={y + 0.25}
									width={CELL - 0.5}
									height={CELL - 0.5}
									rx={1.4}
									className={
										start
											? "fill-emerald-300/80 dark:fill-emerald-500/30"
											: finish
												? "fill-amber-300/80 dark:fill-amber-400/30"
												: tinted
													? ROW_TINTS[row % ROW_TINTS.length]
													: "fill-white/80 dark:fill-white/[0.07]"
									}
								/>
								{finish && (
									<>
										{/* A little chequered flag corner for the last square. */}
										{[0, 1, 2, 3].map((n) => (
											<rect
												key={n}
												x={x + 0.8 + (n % 2) * 1.5}
												y={y + 0.8 + Math.floor(n / 2) * 1.5}
												width={1.5}
												height={1.5}
												className={n === 0 || n === 3 ? "fill-slate-800/70" : "fill-white/80"}
											/>
										))}
									</>
								)}
								{start && (
									<text
										x={centre.x}
										y={y + CELL - 1.1}
										textAnchor="middle"
										fontSize={1.9}
										fontWeight={800}
										className="fill-emerald-800 dark:fill-emerald-300"
									>
										START
									</text>
								)}
								{finish && (
									<text
										x={centre.x}
										y={y + CELL - 1.1}
										textAnchor="middle"
										fontSize={1.9}
										fontWeight={800}
										className="fill-amber-800 dark:fill-amber-300"
									>
										FINISH
									</text>
								)}
								<text
									x={centre.x}
									y={centre.y + (start || finish ? 0.6 : 1.15)}
									textAnchor="middle"
									fontSize={start || finish ? 3 : 3.3}
									fontWeight={800}
									className="fill-indigo-900/60 dark:fill-white/50"
								>
									{square}
								</text>
							</g>
						);
					})}

					{/* Ladders first, snakes over them — a snake lounges on top. */}
					{Object.entries(LADDERS).map(([from, to]) => (
						<Ladder key={`ladder-${from}`} from={Number(from)} to={to} />
					))}
					{Object.entries(SNAKES).map(([from, to], i) => (
						<Snake
							key={`snake-${from}`}
							from={Number(from)}
							to={to}
							tone={SNAKE_TONES[i % SNAKE_TONES.length]}
						/>
					))}

					{/* The counters, rival first so yours is drawn on top of theirs. */}
					<Token square={state.at[rival]} me={false} />
					<Token square={state.at[seat]} me={true} />
				</svg>
			</div>

			<div className="mb-3 flex justify-between text-sm font-semibold">
				<span className="flex items-center gap-1.5">
					<span
						aria-hidden="true"
						className="inline-block h-3.5 w-3.5 rounded-full shadow ring-2 ring-white/80"
						style={{ background: "var(--color-p1)" }}
					/>
					You — square {state.at[seat]}
				</span>
				<span className="flex items-center gap-1.5">
					<span
						aria-hidden="true"
						className="inline-block h-3.5 w-3.5 rounded-full shadow ring-2 ring-white/80"
						style={{ background: "var(--color-p2)" }}
					/>
					Them — {state.at[rival]}
				</span>
			</div>

			<p className="mb-3 text-center text-sm opacity-70">{state.lastNote}</p>

			{state.offer === null ? (
				<button
					type="button"
					disabled={!myTurn}
					onClick={() => onMove({ roll: true })}
					className="w-full rounded-2xl bg-indigo-600 px-4 py-4 text-2xl font-black text-white shadow transition hover:bg-indigo-500 active:scale-[0.98] disabled:opacity-40"
				>
					🎲 Roll two dice
				</button>
			) : (
				<div className="flex justify-center gap-4">
					{([0, 1] as const).map((use) => {
						const lands = landingFor(state, seat, use);
						const step = state.offer?.[use] ?? 0;
						const climbs = lands > state.at[seat] + step;
						const bitten = lands < state.at[seat] + step;
						return (
							<button
								key={use}
								type="button"
								disabled={!myTurn}
								onClick={() => onMove({ use })}
								aria-label={`Move ${step}, landing on ${lands}`}
								className="flex h-28 w-28 flex-col items-center justify-center rounded-2xl bg-white text-indigo-950 shadow transition hover:-translate-y-0.5 hover:shadow-lg active:scale-95 disabled:opacity-40 dark:bg-white/15 dark:text-white"
							>
								{/* Both dice tumble together, then you choose one — so the
								    cube is what you are picking between, not just a picture
								    of it. The number stays in the label underneath. */}
								<Die value={step} roll={state.rollsUsed} size="3rem" />
								<span className="mt-1 text-xs font-semibold">
									{step} — {climbs ? "🪜 up!" : bitten ? "🐍 down" : `to ${lands}`}
								</span>
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
}

// --- Turbo Track ----------------------------------------------------------

export function TurboTrackBoard({ state, seat, onMove }: BoardProps<TurboTrackState>) {
	const myTurn = state.turn === seat && !state.result;
	const rival: PlayerIndex = seat === 0 ? 1 : 0;
	const recovering = state.spinning[seat];

	return (
		<div className="board-frame board-md">
			<div className="mb-4 flex flex-wrap justify-center gap-[3px]">
				{Array.from({ length: TRACK_LENGTH }, (_, index) => {
					const square = index + 1;
					const mine = state.at[seat] === square;
					const theirs = state.at[rival] === square;
					const bend = (CORNERS as readonly number[]).includes(square);
					return (
						<div
							key={square}
							className={`flex h-7 w-7 items-center justify-center rounded text-sm ${
								mine
									? "bg-rose-400"
									: theirs
										? "bg-sky-400"
										: bend
											? "bg-amber-300 dark:bg-amber-800"
											: "bg-white/60 dark:bg-white/10"
							}`}
						>
							{mine ? "🏎️" : theirs ? "🚙" : bend ? "⚠️" : ""}
						</div>
					);
				})}
			</div>

			<p className="mb-3 text-center text-sm opacity-70">{state.lastNote}</p>

			<div className="grid grid-cols-3 gap-3">
				{GEARS.map((option) => {
					const target = Math.min(TRACK_LENGTH, state.at[seat] + option.moves);
					const bends = cornersCrossed(state.at[seat], target);
					return (
						<button
							key={option.gear}
							type="button"
							disabled={!myTurn}
							onClick={() => onMove({ gear: option.gear })}
							className="flex flex-col items-center rounded-2xl bg-white px-2 py-3 text-indigo-950 shadow disabled:opacity-40 dark:bg-white/15 dark:text-white"
						>
							<span className="text-2xl font-black">{option.gear}</span>
							<span className="text-sm font-bold">{option.label}</span>
							<span className="text-xs opacity-70">
								{bends === 0 ? "clear road" : `${bends} bend${bends > 1 ? "s" : ""}`}
							</span>
						</button>
					);
				})}
			</div>

			{recovering && (
				<p className="mt-3 text-center font-semibold text-amber-700 dark:text-amber-300">
					You spun — this turn gets you straight again.
				</p>
			)}
		</div>
	);
}
